import React, { useEffect, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import Alert from '@xo-union/tk-component-alerts';
import Modal from '@xo-union/tk-component-modals';
import { Body1 } from '@xo-union/tk-ui-typography';
import { compose } from '@xo-union/react-css-modules';
import Icon from '@xo-union/tk-component-icons';
import { Tooltip, TooltipContainer } from '@xo-union/tk-component-tooltips';
import { FlexImageRotator, RotatorSave } from '../../../libs/gs-component-image-upload';
import Spinner from '@xo-union/tk-component-spinner';
import { FocusPicker, Focus } from 'image-focus';
import styles from './index.scss';
import PhotoRotation from './PhotoRotation';
import PhotoActions from './PhotoActions';
import { SaveImageProps } from '../../LiteHomePageCard/CoverPhotoPortrait';
import retina from './square.svg';
import useIsMounted from '../../../helpers/useIsMounted';

interface Props {
  uploadFile: HTMLImageElement;
  saveImage: (props: SaveImageProps) => void;
  onCancel: () => void;
  primaryActionText?: string;
  secondaryActionText?: string;
  titleText?: string;
  loading?: boolean;
  children?: React.ReactNode | React.ReactNode[] | null;
  focalPoint?: SaveImageProps['focalPoint'];
  ref?: any;
}

const getFocalPoint = (focalPoint: SaveImageProps['focalPoint']): Focus => {
  if (!focalPoint) return { x: 0, y: 0 };

  return { x: +focalPoint[0], y: +focalPoint[1] };
};

const addTestIdToFocalFrame = (imgEl: HTMLImageElement) => {
  const focalFrameEl = imgEl.nextElementSibling;
  if (focalFrameEl) {
    focalFrameEl.setAttribute('data-testid', 'cover-photo-focalframe');
  }
};

const PhotoUploadModal: React.FC<Props> = React.forwardRef(
  (
    {
      uploadFile,
      saveImage,
      onCancel,
      primaryActionText,
      secondaryActionText,
      titleText = 'Upload Photo',
      loading,
      children,
      focalPoint = ['0', '0'],
    },
    ref
  ) => {
    const localRef = React.useRef<any>(null);
    const alertRef = ref || localRef;
    const classes = compose({
      'close-button': styles.closeButton,
      medium: styles.modal,
    });
    const [focus, setFocus] = useState<Focus>(() => getFocalPoint(focalPoint));
    const isMounted = useIsMounted();

    const debouncedChangeHandler = useMemo(() => {
      const changeHandler = (newFocus: Focus) => {
        if (isMounted()) {
          setFocus({ x: +newFocus.x.toFixed(2), y: +newFocus.y.toFixed(2) });
        }
      };

      return debounce(changeHandler, 300);
    }, [isMounted]);

    const onSaveImage = (info: RotatorSave) => {
      const image: SaveImageProps = {
        ...info,
        focalPoint: [focus.x.toString(), focus.y.toString()],
      };
      saveImage(image);
    };

    useEffect(() => {
      const focusPickerEl = document.querySelector('[class$="focused-cover-photo"]:first-child') as HTMLImageElement;

      if (focusPickerEl) {
        const focusPicker = new FocusPicker(focusPickerEl, {
          retina,
          focus,
          onChange: debouncedChangeHandler,
        });

        if (isMounted()) addTestIdToFocalFrame(focusPickerEl);

        return () => focusPicker.disable();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Modal classes={classes} className={styles.modal} size="lg">
        <FlexImageRotator
          imageContainerStyles={styles.imageContainerInModal}
          file={uploadFile}
          onSave={onSaveImage}
          imageStyles="focused-cover-photo"
        >
          {image => (
            <>
              <div className={styles.header}>
                <div className={styles.title}>{titleText}</div>
                <div className={styles.options}>
                  <PhotoRotation />
                </div>
              </div>

              <div className={styles.main}>{image}</div>
              <Body1>Select the focus of your photo to make sure it’s visible on all screen sizes.</Body1>
              <div className={styles.footer}>
                {loading ? (
                  <div className={styles.loading}>
                    <Spinner data-testid="loading-spinner" />
                  </div>
                ) : (
                  <>
                    {children}
                    <TooltipContainer className={styles.tooltipContainer}>
                      <div data-testid="edit-photo-tips" className={styles.photoTips}>
                        <Icon name="info" size="md" />
                      </div>
                      <Tooltip
                        data-testid="edit-photo-tooltip"
                        arrowPosition="top"
                        arrowOffset={15}
                        className={styles.tooltip}
                        style={{ width: '240px', left: '-25px' }}
                      >
                        We’ll automatically crop and adjust your photo to fit on all screen sizes.
                      </Tooltip>
                    </TooltipContainer>

                    <PhotoActions
                      primaryActionText={primaryActionText}
                      secondaryActionText={secondaryActionText}
                      onCancel={onCancel}
                    />
                  </>
                )}
                <Alert ref={alertRef} position="bottom" />
              </div>
            </>
          )}
        </FlexImageRotator>
      </Modal>
    );
  }
);

export default PhotoUploadModal;
